import { ReactNode, useCallback } from "react"

import { CircularProgress, Modal, Typography } from "@mui/material"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { ArrowBackIcon } from "@l2r-front/l2r-icons"

import { I18N_NAMESPACE } from "../../constants/i18n"
import { useIsMobileDevice } from "../../hooks/useIsMobileDevice"

import * as Styled from "./UiModal.styled"

type UiModalProps = {
    id?: string,
    buttons?: ReactNode,
    className?: string,
    children: ReactNode,
    desc?: string,
    handleBack?: () => void,
    height?: number | string,
    open: boolean,
    onClose: () => void,
    title: string,
    titleIcon?: ReactNode,
    width?: number | string,
    showLoader?: boolean,
}

export function UiModal(props: UiModalProps) {

    const {
        id = "ui-modal",
        buttons = null,
        className,
        children,
        desc,
        handleBack,
        height = "80%",
        showLoader = false,
        open,
        onClose,
        title,
        titleIcon = null,
        width = "80%",
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const isMobile = useIsMobileDevice()

    const handleClose = useCallback(() => {
        if (!showLoader) {
            onClose()
        }
    }, [showLoader, onClose])

    return <Modal
        id={id}
        open={open}
        onClose={handleClose}
    >
        <Styled.Box height={height} width={width} isMobile={isMobile} className={className}>
            <Styled.Header>
                {handleBack && <Styled.BackButton color="common/black_states" onClick={() => handleBack?.()}>
                    <ArrowBackIcon color="common/black_states" />
                </Styled.BackButton>}
                {titleIcon && <Styled.TitleIcon>{titleIcon}</Styled.TitleIcon>}
                <Styled.Title variant="Desktop & Mobile/Heading/H1" noWrap>{title}</Styled.Title>
                <Styled.IconButton id="close-button" onClick={handleClose}>
                    <Styled.CloseIcon />
                </Styled.IconButton>
            </Styled.Header>
            {desc && <Styled.Desc variant="Desktop & Mobile/Heading/H4">{desc}</Styled.Desc>}
            <Styled.Content>
                {buttons ? <Styled.Layout buttons={buttons} content={children} /> : children}
            </Styled.Content>
            {showLoader && <Styled.LoaderMask>
                <Styled.LoaderContainer>
                    <CircularProgress />
                    <Typography variant="Desktop & Mobile/Body/Small">{t(I18N_NAMESPACE, "containers.modal.loading")}</Typography>
                </Styled.LoaderContainer>
            </Styled.LoaderMask>}
        </Styled.Box>
    </Modal>
}
